// Pure URL helpers used by SEOHead to build canonical + hreflang links
// and by LanguageContext for language-switching. Extracted so they can
// be unit-tested without React/jsdom.

export const SUPPORTED_LANGUAGES = ['en', 'tr', 'nl', 'fr', 'ar'] as const;
export type SupportedLang = typeof SUPPORTED_LANGUAGES[number];
export const SEO_BASE_URL = 'https://hairartclinics.net';

// Strip a leading /{lang} segment when — and only when — it sits on a
// path boundary. `/enter` must survive; `/en/treatments` becomes
// `/treatments`; `/en` (bare) becomes `/`.
export const removeLanguagePrefix = (pathname: string): string => {
  const stripped = pathname.replace(/^\/(en|tr|nl|fr|ar)(?=\/|$)/, '');
  return stripped === '' ? '/' : stripped;
};

// Build the canonical URL for a specific language, matching sitemap.xml:
//   - English is the default and has no prefix.
//   - The homepage keeps its trailing slash in every language (`/`, `/nl/`).
//   - Deeper paths never carry a trailing slash beyond what they had.
export const buildUrlWithLang = (
  pathnameWithoutLang: string,
  lang: SupportedLang,
  baseUrl: string = SEO_BASE_URL,
): string => {
  pathnameWithoutLang = removeLanguagePrefix(pathnameWithoutLang).split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  if (pathnameWithoutLang === '/') {
    return lang === 'en' ? `${baseUrl}/` : `${baseUrl}/${lang}/`;
  }
  if (lang === 'en') return `${baseUrl}${pathnameWithoutLang}`;
  return `${baseUrl}/${lang}${pathnameWithoutLang}`;
};

// Full set of hreflang entries for a given path, including x-default → en.
export type HreflangTag = 'en' | 'tr' | 'nl' | 'fr' | 'ar' | 'x-default';

export const buildHreflangSet = (
  pathname: string,
  baseUrl: string = SEO_BASE_URL,
): Array<{ hreflang: HreflangTag; href: string }> => {
  const cleanPath = removeLanguagePrefix(pathname);
  const alts: Array<{ hreflang: HreflangTag; href: string }> = SUPPORTED_LANGUAGES.map((lang) => ({
    hreflang: lang,
    href: buildUrlWithLang(cleanPath, lang, baseUrl),
  }));
  alts.push({ hreflang: 'x-default', href: buildUrlWithLang(cleanPath, 'en', baseUrl) });
  return alts;
};
